import './style.scss';

import type { Article } from '@/shared/typings/types/guide';
import { isoTimestamp } from "@/shared/utils/helpers";
import { Frame, Navigation, Text } from '@shopify/polaris';
import { PlusCircleIcon, SettingsIcon } from '@shopify/polaris-icons';
import { doc, getDoc } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { db } from '../../firebase/init';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getArticles, setArticle } from '../../redux/slices/articles';
import { getRequestShop } from '../../redux/slices/env';
import { getNavigation } from '../../redux/slices/navigation';
import ArticleComponent from './Article';
import NoArticles from './NoArticles';

const Articles = () => {
  const shop = useAppSelector(getRequestShop);
  const { articleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const historyReplace = useCallback((path: string) => navigate(path, { replace: true }), [navigate]);

  const navigationState = useAppSelector(getNavigation);
  const {
    sections,
    navigation_items = [],
    navigation_items_unassigned,
    pinned_articles = [],
    first_article_id,
    articles_index = {},
    _init
  } = navigationState;

  const articles = useAppSelector(getArticles);
  const article = articles[articleId || ''];

  const isNew = articleId === 'new';
  const articleRef = articleId ? `shops/${shop}/articles/${articleId}` : null;

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [loading, setLoading] = useState(isNew ? false : !article);
  const [lastFetchedId, setLastFetchedId] = useState<string | null>(null);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);

  const toggleMobileNavigationActive = useCallback(() => setMobileNavigationActive(x => !x), []);
  const handleSectionChange = useCallback((id: string) => {
    setActiveSection(prev => prev === id ? null : id);
  }, []);

  const defaultArticle: Article = useMemo(() => ({ id: '', html: '', title: '', section: '', pinned: false, updated: isoTimestamp() }), []);

  const firstArticlePath = useMemo(() => {
    if (first_article_id) return `/articles/${first_article_id}`;
    if (navigation_items_unassigned && navigation_items_unassigned.length > 0) {
      return `/articles/${navigation_items_unassigned[0].id}`;
    }
    return '/articles';
  }, [first_article_id, navigation_items_unassigned]);

  const sections_list = useMemo(() => {
    let list = Object.keys(sections || {});
    return list;
  }, [sections]);

  const navItems = useMemo(() => {
    const formatNavItem = (i: any) => {
      const isUnassigned = i.id === '__unassigned';
      const itemCount = (navigation_items_unassigned && navigation_items_unassigned.length > 0)
        ? (navigation_items_unassigned[0].subNavigationItems?.length || 0)
        : 0;

      return {
        ...i,
        label: !isUnassigned ? i.label : <Text as="span" tone="subdued">{`Ungrouped articles (${itemCount})`}</Text>,
        subNavigationItems: (i.subNavigationItems || []).map((n: any) => ({
          ...n,
          label: n.label || '[add title]',
          selected: n.id === articleId
        })),
        onClick: () => handleSectionChange(i.id),
        url: i.id === activeSection ? '/' : undefined,
        ...(!isUnassigned && {
          secondaryActions: [
            {
              // url: `/articles/new?section=${i.id}`,
              icon: PlusCircleIcon,
              accessibilityLabel: 'Add article',
              onClick: () => {
                navigate(`/articles/new?section=${i.id}`);
              }
            }
          ]

        })
      };
    };

    const items = [
      ...navigation_items.map(formatNavItem),
      ...(navigation_items_unassigned
        ? navigation_items_unassigned.map(formatNavItem)
        : [])
    ];

    // items.push({
    //   id: 'add-guide',
    //   index: -1,
    //   label: 'Add article',
    //   icon: PlusCircleIcon,
    //   onClick: () => {
    //     dispatch(setArticle(defaultArticle));
    //     navigate(`/articles/new?section=${sections_list[0] || ''}`);
    //   }
    // });

    return items;
  }, [
    navigation_items,
    navigation_items_unassigned,
    activeSection,
    handleSectionChange,
    sections_list,
    navigate,
    dispatch,
    defaultArticle,
    articleId
  ]);

  useEffect(() => {
    if (articleId && articles_index[articleId]) {
      let sectionId = articles_index[articleId].section;
      sectionId = sections[sectionId] ? sectionId : '__unassigned';
      setActiveSection(sectionId);
    }
  }, [articleId, articles_index, sections]);

  useEffect(() => {
    if (isNew) {
      dispatch(setArticle(defaultArticle));
    }
  }, [isNew, defaultArticle]);

  useEffect(() => {
    if (isNew) setLoading(false);
    if (articleId && _init && articles_index[articleId] && !isNew) {
      if (lastFetchedId !== articleId && articleRef) {
        window.scrollTo(0, 0);
        setLoading(!article);
        setLastFetchedId(articleId);
        getDoc(doc(db, articleRef)).then(s => {
          let a = s.data() as Article;
          setLoading(false);
          dispatch(setArticle({ ...a, id: s.id }));
        }).catch((err) => {
          console.error("Error fetching article:", err);
          setLoading(false);
        });
      }
    } else if (_init && !isNew && firstArticlePath !== window.location.pathname) {
      historyReplace(firstArticlePath);
    }
  }, [article, articleRef, articleId, isNew, lastFetchedId, _init, articles_index, historyReplace, firstArticlePath, dispatch, defaultArticle]);

  return (
    <div className={`AC-Article__Wrap ${articleId ? ' Article--Active' : ''}`}>
      <Frame
        navigation={
          <NavigationMarkup
            items={navItems}
            pinnedItems={pinned_articles.map((p: any) => ({
              ...p,
              selected: p.id === articleId
            }))}
            location={location.pathname}
          />
        }
        showMobileNavigation={mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigationActive}
      >
        {articleId
          ? <ArticleComponent
            key={articleId}
            article={(isNew ? defaultArticle : article) || defaultArticle}
            articleId={articleId}
            articleRef={articleRef}
            loading={loading}
            isNew={isNew}
            firstArticlePath={firstArticlePath}
            onActivateMobileNavigation={toggleMobileNavigationActive}
            historyReplace={historyReplace}
          />
          : <NoArticles />
        }
      </Frame>
    </div>
  );
}

const NavigationMarkup = ({ items, pinnedItems, location }: { items: any[], pinnedItems: any[], location: string }) => {
  return (
    <Navigation location={location}>
      {pinnedItems.length > 0 &&
        <Navigation.Section
          title="Pinned"
          items={pinnedItems}
        />
      }
      <Navigation.Section
        separator={pinnedItems.length > 0}
        items={items}
      />
      <div style={{ height: '100%' }}></div>
      <Navigation.Section
        items={[{ label: 'Settings', icon: SettingsIcon, url: '/settings' }]}
      />
    </Navigation>
  );
};

export default Articles;
